import {createStyles} from '@mantine/core';
import classNames from 'classnames';
import React from 'react';

import {sortErrors} from 'notebook/tables/excess/Util';
import {BaseCellError, TriangleState} from 'notebook/tables/TableConstants';
import {FloatingPlacement} from 'ui-library/floating-ui/FloatingConstants';
import Popover from 'ui-library/floating-ui/Popover';

import {EvaluatedCellError} from '../excess/Evaluator';

type VisibleTriangleState = Exclude<TriangleState, TriangleState.VALID_NO_TRIANGLE>;

const LABEL_CLASS_NAMES: Record<VisibleTriangleState, string> = {
  [TriangleState.VALID_WITH_TRIANGLE]: 'label-info',
  [TriangleState.INVALID]: 'label-warning',
  [TriangleState.PARTIALLY_VALID]: 'label-warning',
  [TriangleState.ILLEGAL]: 'label-danger',
  [TriangleState.OUTDATED]: 'label-outdated',
  [TriangleState.WARNING]: 'label-warning',
  [TriangleState.UNSUBMITTED_CHANGES]: 'label-info',
};

const useStyles = createStyles((theme) => ({
  container: {display: 'flex', flexDirection: 'column', gap: theme.spacing.xs},
  item: {marginBottom: theme.spacing.xs},
}));

interface Props {
  error: EvaluatedCellError;
  // This serves as the default label class for the errors.
  // If individual errors have an errorState, then that will be used to determine the label color
  errorState: VisibleTriangleState;
  children: JSX.Element;
  placement?: FloatingPlacement;
}

function ErrorTrianglePopover(props: Props): JSX.Element {
  const {classes} = useStyles();
  const defaultLabelClass = LABEL_CLASS_NAMES[props.errorState];
  const sortedErrors = sortErrors(props.error);
  const errors: Array<BaseCellError> = Array.isArray(sortedErrors) ? sortedErrors : [sortedErrors];
  return (
    <Popover
      placement={props.placement}
      renderContent={() => (
        <div className={classes.container}>
          {errors.map((error) => {
            const message = error.message;
            const title = error.type;
            const hasMessage = Boolean(message) && message.length > 0;
            const labelClass =
              (error.errorState != null
                ? LABEL_CLASS_NAMES[error.errorState as VisibleTriangleState]
                : undefined) ?? defaultLabelClass;
            return (
              <div key={error.message} className={classes.item}>
                <div
                  className={classNames('label', labelClass, {
                    'u-tiny-spacing-right': hasMessage,
                  })}
                >
                  {title}
                </div>

                {hasMessage && message}
              </div>
            );
          })}
        </div>
      )}
    >
      {props.children}
    </Popover>
  );
}
ErrorTrianglePopover.displayName = 'ErrorTrianglePopover';

export default ErrorTrianglePopover;
